import { HORIZONTAL_GAP, PADDING, getNodeDimensions } from '../constants';
import { extractCornerWaypoints, translatePathD, warpPathToNodeDeltas } from '../elkEdgePathAdapter';
import { findDisconnectedComponents } from '../index';
import { buildDependencyGraph } from '../graphBuilder';
import { getNonMilestoneTaskIds } from '../../utils/milestoneUtils';

export function getBoundingBox(positions, nodeDimensions) {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  Object.entries(positions).forEach(([taskId, pos]) => {
    if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) return;
    const dims = getNodeDimensions(taskId, nodeDimensions);
    minX = Math.min(minX, pos.x);
    maxX = Math.max(maxX, pos.x + dims.width);
    minY = Math.min(minY, pos.y);
    maxY = Math.max(maxY, pos.y + dims.height);
  });

  if (!Number.isFinite(minX)) {
    return { minX: PADDING, maxX: PADDING, minY: PADDING, maxY: PADDING, width: 0, height: 0 };
  }

  return { minX, maxX, minY, maxY, width: maxX - minX, height: maxY - minY };
}

export function getChainBraceBoundingBox(positions, nodeDimensions, componentTasks) {
  const braceIds = new Set(getNonMilestoneTaskIds(componentTasks));
  const filtered = Object.fromEntries(
    Object.entries(positions).filter(([id]) => braceIds.has(id)),
  );
  return getBoundingBox(filtered, nodeDimensions);
}

function nodeRects(taskIds, positions, nodeDimensions) {
  const rects = [];
  taskIds.forEach((id) => {
    const pos = positions[id];
    if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) return;
    const dims = getNodeDimensions(id, nodeDimensions);
    rects.push({
      x: pos.x,
      y: pos.y,
      right: pos.x + dims.width,
      bottom: pos.y + dims.height,
    });
  });
  return rects;
}

function rectsFromPath(pathD) {
  const points = extractCornerWaypoints(pathD);
  const rects = [];
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    const top = Math.min(a.y, b.y);
    const height = Math.abs(b.y - a.y);
    rects.push({
      x: Math.min(a.x, b.x),
      y: top,
      right: Math.max(a.x, b.x),
      bottom: top + (height < 0.5 ? 0.5 : height),
    });
  }
  return rects;
}

/**
 * How far `moving` must travel right so it clears `obstacle` by HORIZONTAL_GAP.
 * Zero when they don't share a vertical band, or when `moving` already sits
 * fully to one side of the obstacle.
 */
function rightShiftToClear(moving, obstacle) {
  const sharesY = moving.y < obstacle.bottom && moving.bottom > obstacle.y;
  if (!sharesY) return 0;
  if (moving.x >= obstacle.right + HORIZONTAL_GAP - 0.5) return 0;
  if (moving.right + HORIZONTAL_GAP <= obstacle.x + 0.5) return 0;
  return obstacle.right + HORIZONTAL_GAP - moving.x;
}

function minNodeX(component, positions) {
  let min = Infinity;
  component.tasks.forEach((task) => {
    const pos = positions[task.id];
    if (pos && Number.isFinite(pos.x)) min = Math.min(min, pos.x);
  });
  return min;
}

/**
 * Connection-separate workflows each keep the layout ELK gave them.
 * Place them left to right and, where a later workflow's cards or connector
 * lines would touch an earlier one, slide that whole workflow right.
 */
export function spaceSeparateWorkflows(positions, edgePaths, links, tasks, nodeDimensions) {
  if (!positions || !tasks || tasks.length < 2) return;

  const components = findDisconnectedComponents(tasks, buildDependencyGraph(tasks));
  if (components.length < 2) return;

  const edgeIdsBySource = new Map();
  (links || []).forEach((link) => {
    if (!link?.id || !link.source || !edgePaths?.[link.id]) return;
    if (!edgeIdsBySource.has(link.source)) edgeIdsBySource.set(link.source, []);
    edgeIdsBySource.get(link.source).push(link.id);
  });

  const ordered = components
    .filter((component) => Number.isFinite(minNodeX(component, positions)))
    .sort((a, b) => {
      const dx = minNodeX(a, positions) - minNodeX(b, positions);
      if (Math.abs(dx) > 0.5) return dx;
      return (a.id || '').localeCompare(b.id || '');
    });

  const placed = [];
  ordered.forEach((component) => {
    const taskIds = component.tasks.map((task) => task.id);
    const edgeIds = [];
    taskIds.forEach((id) => {
      const owned = edgeIdsBySource.get(id);
      if (owned) owned.forEach((edgeId) => edgeIds.push(edgeId));
    });

    const moving = nodeRects(taskIds, positions, nodeDimensions);
    edgeIds.forEach((edgeId) => {
      rectsFromPath(edgePaths[edgeId]).forEach((rect) => moving.push(rect));
    });
    if (!moving.length) return;

    let shift = 0;
    moving.forEach((rect) => {
      placed.forEach((obstacle) => {
        const need = rightShiftToClear(rect, obstacle);
        if (need > shift) shift = need;
      });
    });

    if (shift > 0.5) {
      taskIds.forEach((id) => {
        const pos = positions[id];
        if (!pos) return;
        positions[id] = { x: pos.x + shift, y: pos.y };
      });
      edgeIds.forEach((edgeId) => {
        const next = translatePathD(edgePaths[edgeId], shift, 0);
        if (next) edgePaths[edgeId] = next;
      });
    }

    const dx = shift > 0.5 ? shift : 0;
    moving.forEach((rect) => {
      placed.push({
        x: rect.x + dx,
        y: rect.y,
        right: rect.right + dx,
        bottom: rect.bottom,
      });
    });
  });
}

export function snapshotPositions(positions) {
  const before = {};
  Object.entries(positions).forEach(([id, pos]) => {
    if (pos && Number.isFinite(pos.x) && Number.isFinite(pos.y)) {
      before[id] = { x: pos.x, y: pos.y };
    }
  });
  return before;
}

export function warpEdgesToMovedNodes(edgePaths, links, before, positions) {
  (links || []).forEach((link) => {
    const path = edgePaths[link.id];
    const fromSource = before[link.source];
    const fromTarget = before[link.target];
    const toSource = positions[link.source];
    const toTarget = positions[link.target];
    if (!path || !fromSource || !fromTarget || !toSource || !toTarget) return;
    const warped = warpPathToNodeDeltas(
      path,
      { x: toSource.x - fromSource.x, y: toSource.y - fromSource.y },
      { x: toTarget.x - fromTarget.x, y: toTarget.y - fromTarget.y },
    );
    if (warped) edgePaths[link.id] = warped;
  });
}

export function refreshChainInfo(chainInfo, positions, nodeDimensions) {
  chainInfo.forEach((chain) => {
    const subset = {};
    (chain.taskIds || []).forEach((id) => {
      if (positions[id]) subset[id] = positions[id];
    });
    const bbox = getBoundingBox(subset, nodeDimensions);
    chain.position = { x: bbox.minX, y: bbox.minY };
    chain.width = bbox.width;
  });
}
